import moment from "moment";
import { IGraphData, IGraphContentData, IGraphDataGender, IGraphHeatmapData } from '../interfaces/types';
import { EStatisticsType, IAttendanceFilter, IGenderFilter, EStatisticsGender, EStatisticsGenderType, EStatisticsDateType, EStatisticsInterval } from '../../reducers/data';
import { t } from '@lingui/macro';

export function getRandomColors(cnt: number) {
    let colors: string[] = []

    if (cnt < 1) {
        return []
    }

    for (let index = 1; index <= cnt; index++) {
        let perc = ((index % 2) * 50) + 25;
        let perc2 = 100 - ( ((index + 1) % 2) * 40 )
        colors.push("hsl(" + (index * (360 / cnt) % 360) + "," + perc2 + "%," + perc + "%)")
    }

    return colors
}

export function getDaysBetweenDates(startDate: string, endDate: string) {
    let start = moment(startDate)
    let end = moment(endDate)

    let now = start.clone()
    let dates = []

    while (now.isSameOrBefore(end)) {
        dates.push(now.format('YYYY-MM-DD'))
        now.add(1, 'days')
    }

    return dates
}

export function getYearsBetweenDates(startDate: string, endDate: string) {
    let start = moment(startDate)
    let end = moment(endDate)

    let now = start.clone()
    let dates = []

    while (now.isSameOrBefore(end)) {
        dates.push(now.format('YYYY'))
        now.add(1, 'year')
    }

    return dates
}

export function getDateRangeTitle(startDate: string, endDate: string, interval: EStatisticsInterval, i18n: any) {
    let text = moment(startDate).format('DD. MM. YYYY')

    let days = moment(endDate).diff(moment(startDate), 'days')
    let minutes = moment(endDate).diff(moment(startDate), 'minutes')

    if (startDate !== endDate && (days !== 1)) {
        text += ' - ' + moment(endDate).format('DD. MM. YYYY')
    }

    if (days > 1 && days <= 25) {
        text = t`Týždeň` + ' ' + moment(startDate).week() + '-' + moment(startDate).year()
    }

    if (days >= 26 && days <= 35) {
        text = moment(startDate).format('MMMM') + ' ' + moment(startDate).year()
    }

    if (days >= 100) {
        text = moment(startDate).year().toString()
    }

    if (minutes === 15 || minutes === 30 || minutes === 60) {
        text = moment(startDate).format('DD. MM. YYYY HH:mm:ss') + ' - ' + moment(endDate).format('HH:mm:ss')
    }

    if(interval === EStatisticsInterval.custom) {
        text = moment(startDate).format('DD. MM. YYYY') + ' - ' + moment(endDate).format('DD. MM. YYYY')
    }

    return text
}

export function getFilterDateRangeTitle(startDate: string, endDate: string, i18n: any, filterType: EStatisticsDateType) {
    switch (filterType) {
        case EStatisticsDateType.day:
            return moment(startDate).format('DD. MM. YYYY')
        case EStatisticsDateType.month:
            return moment(startDate).format('MMMM') + ' ' + moment(startDate).year()
        case EStatisticsDateType.week:
            return t`Týždeň` + ' ' + moment(startDate).week() + '-' + moment(startDate).year()
        case EStatisticsDateType.year:
            return moment(startDate).year().toString()
        default:
            if (startDate === endDate) {
                return moment(startDate).format('DD. MM. YYYY')
            }
            return moment(startDate).format('DD. MM. YYYY') + ' - ' + moment(endDate).format('DD. MM. YYYY')
    }
}

export function parseGraphData(graphData: IGraphData[], i18n: any) {
    let retObject: IGraphContentData = {
        series: [],
        categories: []
    }

    graphData.forEach(g => {
        retObject.categories.push(g.name)
    })

    let serie: {
        name: string,
        data: number[]
    } = {
        name: t`Počet návštevníkov`,
        data: []
    }

    graphData.forEach(g => {
        g.data.forEach(d => {
            serie.data.push(Number(d.count))
        })
    })

    retObject.series = [serie]
    retObject.series = []

    graphData.forEach(g => {
        retObject.series.push({
            name: g.name,
            data: g.data.map(d => {
                return [
                    moment(d.start).local().valueOf(),
                    Number(d.count)
                ]
            })
        })
    })

    return retObject
}

/**
 * Overlay mode: strip the date from each point and replace it with a
 * fixed reference date (today) so that all selected days / weeks / months
 * are plotted on the SAME X axis and can be visually compared.
 *
 * Each series keeps its original name (e.g. "14. 01. 2026") so the legend
 * still identifies which day/range each line belongs to.
 *
 * The reference date is always today at 00:00 local time so that ApexCharts
 * formats the X axis labels as HH:mm / day-of-month rather than full dates.
 */
export function parseGraphDataOverlay(graphData: IGraphData[], i18n: any): IGraphContentData {
    const refDate = moment().startOf('day')

    const retObject: IGraphContentData = {
        series: [],
        categories: []
    }

    graphData.forEach(g => {
        retObject.categories.push(g.name)
        retObject.series.push({
            name: g.name,
            data: g.data.map(d => {
                const originalTime = moment(d.start).local()
                // Keep only the time part; apply it to the reference date
                const overlayTs = refDate.clone()
                    .hour(originalTime.hour())
                    .minute(originalTime.minute())
                    .second(originalTime.second())
                    .valueOf()
                return [overlayTs, Number(d.count)]
            })
        })
    })

    return retObject
}

export function parseHeatData(graphData: IGraphData[], i18n: any) {
    let retObject: IGraphHeatmapData = {
        series: [],
        categories: []
    }

    graphData.forEach(g => {
        retObject.categories.push(g.name)
    })

    let serie: {
        name: string,
        data: number[]
    } = {
        name: t`Počet návštevníkov`,
        data: []
    }

    graphData.forEach(g => {
        g.data.forEach(d => {
            serie.data.push(Number(d.count))
        })
    })

    // retObject.series = [serie]
    retObject.series = []

    graphData.forEach(g => {
        retObject.series.push({
            name: g.name,
            data: g.data.map(d => {
                return {
                    x: moment(d.start).local().format('HH:mm'),
                    y: Number(d.count)
                }
            })
        })
    })

    return retObject
}

export function parseGraphDataAttendance(graphData: IGraphData[], filter: IAttendanceFilter, i18n: any) {
    let retObject: IGraphContentData = {
        series: [],
        categories: []
    }

    graphData.forEach(g => {
        retObject.categories.push(g.name)
    })

    let serie: {
        name: string,
        data: number[]
    } = {
        name: t`Počet návštevníkov`,
        data: []
    }

    graphData.forEach(g => {
        g.data.forEach(d => {
            serie.data.push(Number(d.count))
        })
    })

    retObject.series = [serie]

    if (filter.type === EStatisticsType.cake) {
        retObject.series = serie.data
    }

    if (filter.type === EStatisticsType.xy) {
        retObject.series = []

        graphData.forEach(g => {
            retObject.series.push({
                name: g.name,
                data: g.data.map(d => {
                    return [
                        moment(d.start).valueOf(),
                        Number(d.count)
                    ]
                })
            })
        })
    }

    return retObject
}

export function parseGraphDataGender(graphData: IGraphDataGender[], filter: IGenderFilter, i18n: any) {
    const isPercent = filter.genderType === EStatisticsGenderType.percentage
    const showMen   = filter.gender && [EStatisticsGender.all, EStatisticsGender.man].includes(filter.gender)
    const showWomen = filter.gender && [EStatisticsGender.all, EStatisticsGender.woman].includes(filter.gender)

    let retObject: IGraphContentData = {
        series: [],
        categories: []
    }

    // ── Pie / cake ────────────────────────────────────────────────────────────
    if (filter.type === EStatisticsType.cake) {
        let mens = 0
        let womans = 0
        graphData.forEach(g => {
            g.data.forEach(d => {
                if (d.gender === 'M') mens   += Number(d.count)
                else                  womans += Number(d.count)
            })
        })
        retObject.categories = [t`Muži`, t`Ženy`]
        retObject.series     = [mens, womans]
        return retObject
    }

    // ── XY (area/line) ────────────────────────────────────────────────────────
    if (filter.type === EStatisticsType.xy) {
        graphData.forEach(g => {
            const mans   = g.data.filter(d => d.gender === 'M')
            const womans = g.data.filter(d => d.gender === 'F')

            if (mans.length > 0 && showMen) {
                retObject.series.push({
                    name: g.name + ' - ' + t`muži`,
                    data: mans.map(d => [
                        moment(d.start).valueOf(),
                        Number(isPercent ? d.percentage : d.count)
                    ])
                })
            }
            if (womans.length > 0 && showWomen) {
                retObject.series.push({
                    name: g.name + ' - ' + t`ženy`,
                    data: womans.map(d => [
                        moment(d.start).valueOf(),
                        Number(isPercent ? d.percentage : d.count)
                    ])
                })
            }
        })
        // categories unused for datetime XY, but keep names for legend clarity
        retObject.categories = graphData.map(g => g.name)
        return retObject
    }

    // ── Column (stacked bar) ──────────────────────────────────────────────────
    // Layout:
    //   X axis  = one category per group (zone / location / date-range)
    //   Stack   = two series only: Muži (M) + Ženy (F)
    //   Value   = sum of all time-bucket counts for that gender within the group
    //
    // This gives one bar per group, split into M (bottom) + F (top).
    if (filter.type === EStatisticsType.column) {
        // Categories = group names
        retObject.categories = graphData.map(g => g.name)

        // Aggregate M and F totals per group
        const mTotals: number[] = graphData.map(g =>
            g.data
                .filter(d => d.gender === 'M')
                .reduce((sum, d) => sum + Number(isPercent ? d.percentage : d.count), 0)
        )
        const fTotals: number[] = graphData.map(g =>
            g.data
                .filter(d => d.gender === 'F')
                .reduce((sum, d) => sum + Number(isPercent ? d.percentage : d.count), 0)
        )

        if (showMen) {
            retObject.series.push({ name: t`Muži`, data: mTotals })
        }
        if (showWomen) {
            retObject.series.push({ name: t`Ženy`, data: fTotals })
        }
        return retObject
    }

    return retObject
}

export function numberWithSpaces(x: number) {
    return x.toString().replace(/\B(?=(\d{3})+(?!\d))/g, " ")
}

/**
 * Format a dwell-time value in milliseconds to a human-readable string.
 *
 * Examples:
 *   msToTime(90000)   → "1 min 30 s"
 *   msToTime(3720000) → "1 h 2 min"
 *   msToTime(45000)   → "45 s"
 */
export function msToTime(ms: number): string {
    if (!ms || ms <= 0) return '0 s'

    const totalSeconds = Math.round(ms / 1000)
    const hours   = Math.floor(totalSeconds / 3600)
    const minutes = Math.floor((totalSeconds % 3600) / 60)
    const seconds = totalSeconds % 60

    if (hours > 0) {
        return minutes > 0 ? `${hours} h ${minutes} min` : `${hours} h`
    }
    if (minutes > 0) {
        return seconds > 0 ? `${minutes} min ${seconds} s` : `${minutes} min`
    }
    return `${seconds} s`
}

/** @deprecated use msToTime — old version assumed seconds input, data is ms */
export function numberToTime(x: number) {
    return msToTime(x)
}